import React, { useContext, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { User, UserCog } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { AdminAuthContext } from '../context/AdminAuthContext';
import Logo from '../components/ui/Logo';

const Home = () => {
  const { user } = useContext(AuthContext);
  const { admin } = useContext(AdminAuthContext);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Settiamo un piccolo delay per assicurarci che il DOM sia pronto
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Se l'admin è loggato, reindirizza a /admin
  if (admin) {
    return <Navigate to="/admin" replace />;
  }

  // Se l'utente normale è loggato, reindirizza a /dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleNavigation = (path) => {
    setIsExiting(true);
    // Aspettiamo che l'animazione di uscita finisca prima di navigare
    setTimeout(() => {
      navigate(path);
    }, 500); // 500ms = durata della transizione
  };

  return (
    <div className="min-h-screen bg-light-surface dark:bg-dark-surface">
      <div className="container mx-auto px-4 py-16">
        {/* Header con animazione slide-down */}
        <div className={`
          text-center mb-16 
          transition-all duration-500
          ${isLoaded && !isExiting ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}
        `}>
          <div className="flex justify-center mb-6">
            <Logo variant="primary" size="xl" />
          </div>
          <h1 className="text-4xl font-bold text-light-on-surface dark:text-dark-on-surface mb-4">
            myBecco
          </h1>
          <p className="text-light-on-surface/80 dark:text-dark-on-surface/80 text-xl">
            Effettua l'accesso per accedere alle funzionalità
          </p>
        </div>

        {/* Cards Container */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* User Card */}
          <div 
            className={`
              bg-light-surface-container dark:bg-dark-surface-container 
              rounded-lg shadow-surface p-8 
              hover:shadow-surface-bright
              transition-all duration-500
              ${isLoaded && !isExiting ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
            `}
            style={{ 
              transitionDelay: isLoaded && !isExiting ? '400ms' : '0ms',
              visibility: isLoaded ? 'visible' : 'hidden'
            }}
          >
            <div className="text-center">
              <User className="w-16 h-16 mx-auto mb-4 text-light-secondary dark:text-dark-secondary" />
              <h2 className="text-2xl font-semibold text-light-on-surface dark:text-dark-on-surface mb-4">
                Accesso Utenti
              </h2>
              <p className="text-light-on-surface/70 dark:text-dark-on-surface/70 mb-8">
                Mettiti in conttatto con il tuo Becco di fiducia
              </p>
              <button 
                onClick={() => handleNavigation('/login')}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-light-secondary dark:bg-dark-secondary text-light-on-secondary dark:text-dark-on-secondary rounded-md hover:bg-light-secondary-container dark:hover:bg-dark-secondary-container transition-colors"
              >
                <User className="w-4 h-4" />
                Accedi come Utente
              </button>
            </div>
          </div>

          {/* Admin Card */}
          <div 
            className={`
              bg-light-surface-container dark:bg-dark-surface-container 
              rounded-lg shadow-surface p-8 
              hover:shadow-surface-bright
              transition-all duration-500
              ${isLoaded && !isExiting ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
            `}
            style={{ 
              transitionDelay: isLoaded && !isExiting ? '600ms' : '0ms',
              visibility: isLoaded ? 'visible' : 'hidden'
            }}
          >
            <div className="text-center">
              <UserCog className="w-16 h-16 mx-auto mb-4 text-light-primary dark:text-dark-primary" />
              <h2 className="text-2xl font-semibold text-light-on-surface dark:text-dark-on-surface mb-4">
                Accesso Amministratore
              </h2>
              <p className="text-light-on-surface/70 dark:text-dark-on-surface/70 mb-8">
                Organizza e gestisci le attività da Becco 
              </p>
              <button
                onClick={() => handleNavigation('/admin/login')}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-light-primary dark:bg-dark-primary text-light-on-primary dark:text-dark-on-primary rounded-md hover:bg-light-primary-container dark:hover:bg-dark-primary-container transition-colors"
              >
                <UserCog className="w-4 h-4" />
                Accedi come Admin
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;